import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

function StatRow({ label, v1, v2, higherIsBetter = true, format = v => v }) {
  const n1 = parseFloat(v1) || 0, n2 = parseFloat(v2) || 0;
  const p1Better = higherIsBetter ? n1 > n2 : n1 < n2;
  const p2Better = higherIsBetter ? n2 > n1 : n2 < n1;
  const highlight = { color: "#4ade80", fontWeight: "700" };

  return (
    <tr>
      <td style={{ ...(p1Better ? highlight : {}), textAlign: "right" }}>{format(v1 ?? "—")}</td>
      <td style={{ textAlign: "center", color: "#6b7280", fontSize: "0.8rem", padding: "0.5rem 1rem" }}>{label}</td>
      <td style={{ ...(p2Better ? highlight : {}) }}>{format(v2 ?? "—")}</td>
    </tr>
  );
}

function PlayerSearch({ label, onSelect }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (query.length < 2) { setResults([]); return; }
    axios.get(`${API}/api/players`).then(r => {
      const filtered = (r.data.players || []).filter(p =>
        p.name.toLowerCase().includes(query.toLowerCase()) ||
        p.steam_id.includes(query)
      ).slice(0, 8);
      setResults(filtered);
    });
  }, [query]);

  return (
    <div style={{ position: "relative" }}>
      <div style={{ color: "#9ca3af", fontSize: "0.8rem", marginBottom: "0.4rem" }}>{label}</div>
      <input
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search player name or Steam ID..."
        style={{
          width: "100%", background: "#13151f", border: "1px solid #2d3148",
          borderRadius: "6px", padding: "0.6rem 0.8rem", color: "#e5e7eb",
          fontSize: "0.9rem", outline: "none",
        }}
      />
      {results.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
          background: "#1a1d26", border: "1px solid #2d3148", borderRadius: "6px",
          marginTop: "2px", overflow: "hidden",
        }}>
          {results.map(p => (
            <div key={p.steam_id}
              onClick={() => { onSelect(p); setQuery(p.name); setResults([]); }}
              style={{ padding: "0.6rem 0.8rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "0.6rem" }}
              onMouseEnter={e => e.currentTarget.style.background = "#2d3148"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              {p.avatar_url && <img src={p.avatar_url} alt="" style={{ width: 24, height: 24, borderRadius: "50%" }} />}
              <div>
                <div style={{ fontSize: "0.9rem" }}>{p.name}</div>
                <div style={{ fontSize: "0.7rem", color: "#6b7280" }}>{p.steam_id}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PlayerCard({ data, side }) {
  if (!data) return null;
  const { player, career } = data;
  const borderColor = side === 1 ? "#3b82f6" : "#ef4444";
  return (
    <div className="card" style={{ borderTop: `3px solid ${borderColor}`, textAlign: "center" }}>
      {player.avatar_url ? (
        <img src={player.avatar_url} alt={player.name}
          style={{ width: 64, height: 64, borderRadius: "50%", border: `2px solid ${borderColor}`, marginBottom: "0.5rem" }} />
      ) : (
        <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#2d3148", margin: "0 auto 0.5rem", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem" }}>🎮</div>
      )}
      <div style={{ fontWeight: "700", fontSize: "1.1rem" }}>{player.name}</div>
      <div style={{ color: "#6b7280", fontSize: "0.75rem" }}>{player.steam_id}</div>
      <div style={{ color: "#f0a500", fontSize: "1.4rem", fontWeight: "800", marginTop: "0.5rem" }}>{career?.rating}</div>
      <div style={{ color: "#9ca3af", fontSize: "0.75rem" }}>Rating</div>
    </div>
  );
}

export default function Compare() {
  const [player1, setPlayer1] = useState(null);
  const [player2, setPlayer2] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("stats"); // stats | h2h | form

  useEffect(() => {
    if (!player1 || !player2) return;
    setLoading(true);
    axios.get(`${API}/api/compare/players?p1=${player1.steam_id}&p2=${player2.steam_id}`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [player1, player2]);

  const btnStyle = (active) => ({
    padding: "0.4rem 1.2rem", borderRadius: "6px", border: "none",
    cursor: "pointer", fontWeight: active ? "700" : "400",
    background: active ? "#f0a500" : "#2d3148",
    color: active ? "#0f1117" : "#e5e7eb",
  });

  const c1 = data?.player1?.career;
  const c2 = data?.player2?.career;

  return (
    <div>
      <h1>⚔️ Player Comparison</h1>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1.5rem" }}>
        <PlayerSearch label="Player 1" onSelect={setPlayer1} />
        <PlayerSearch label="Player 2" onSelect={setPlayer2} />
      </div>

      {(!player1 || !player2) && (
        <div className="card" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
          Search for two players above to compare their stats
        </div>
      )}

      {loading && <div className="card" style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>Loading...</div>}

      {data && !loading && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
            <PlayerCard data={data.player1} side={1} />
            <PlayerCard data={data.player2} side={2} />
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1rem" }}>
            <button style={btnStyle(tab === "stats")} onClick={() => setTab("stats")}>Career Stats</button>
            <button style={btnStyle(tab === "h2h")} onClick={() => setTab("h2h")}>Head-to-Head</button>
            <button style={btnStyle(tab === "form")} onClick={() => setTab("form")}>Recent Form</button>
          </div>

          {/* Stats Tab */}
          {tab === "stats" && (
            <div className="card">
              <h2 style={{ marginBottom: "1rem" }}>Career Comparison</h2>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ textAlign: "right" }}>{data.player1.player.name}</th>
                    <th style={{ textAlign: "center" }}>Stat</th>
                    <th>{data.player2.player.name}</th>
                  </tr>
                </thead>
                <tbody>
                  <StatRow label="Matches" v1={c1?.matches} v2={c2?.matches} />
                  <StatRow label="Rating" v1={c1?.rating} v2={c2?.rating} format={v => parseFloat(v).toFixed(3)} />
                  <StatRow label="K/D" v1={c1?.kd} v2={c2?.kd} format={v => parseFloat(v).toFixed(2)} />
                  <StatRow label="ADR" v1={c1?.adr} v2={c2?.adr} format={v => parseFloat(v).toFixed(1)} />
                  <StatRow label="KAST %" v1={c1?.kast} v2={c2?.kast} format={v => `${parseFloat(v).toFixed(1)}%`} />
                  <StatRow label="HS %" v1={c1?.hs_pct} v2={c2?.hs_pct} format={v => `${parseFloat(v).toFixed(1)}%`} />
                  <StatRow label="Kills" v1={c1?.kills} v2={c2?.kills} />
                  <StatRow label="Deaths" v1={c1?.deaths} v2={c2?.deaths} higherIsBetter={false} />
                  <StatRow label="Assists" v1={c1?.assists} v2={c2?.assists} />
                  <StatRow label="Aces (5K)" v1={c1?.aces} v2={c2?.aces} />
                  <StatRow label="4K Rounds" v1={c1?.quads} v2={c2?.quads} />
                  <StatRow label="3K Rounds" v1={c1?.triples} v2={c2?.triples} />
                  <StatRow label="2K Rounds" v1={c1?.doubles} v2={c2?.doubles} />
                  <StatRow label="Clutches Won" v1={c1?.clutches_won} v2={c2?.clutches_won} />
                  <StatRow label="First Kills" v1={c1?.first_kills} v2={c2?.first_kills} />
                  <StatRow label="Enemies Flashed" v1={c1?.enemies_flashed} v2={c2?.enemies_flashed} />
                </tbody>
              </table>
            </div>
          )}

          {/* Head-to-Head Tab */}
          {tab === "h2h" && (
            <div className="card">
              <h2 style={{ marginBottom: "1rem" }}>Head-to-Head Record</h2>
              <div style={{ display: "flex", justifyContent: "center", gap: "2rem", marginBottom: "1.5rem" }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2.5rem", fontWeight: "800", color: "#3b82f6" }}>{data.head_to_head.p1_wins}</div>
                  <div style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{data.player1.player.name}</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2.5rem", fontWeight: "800", color: "#6b7280" }}>{data.head_to_head.draws}</div>
                  <div style={{ color: "#9ca3af", fontSize: "0.8rem" }}>Draws</div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2.5rem", fontWeight: "800", color: "#ef4444" }}>{data.head_to_head.p2_wins}</div>
                  <div style={{ color: "#9ca3af", fontSize: "0.8rem" }}>{data.player2.player.name}</div>
                </div>
              </div>

              {data.head_to_head.matches.length === 0 ? (
                <p style={{ color: "#9ca3af", textAlign: "center" }}>No shared matches found.</p>
              ) : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Map</th>
                      <th>Score</th>
                      <th>{data.player1.player.name}</th>
                      <th>{data.player2.player.name}</th>
                      <th>Date</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.head_to_head.matches.map(m => (
                      <tr key={m.id}>
                        <td style={{ fontWeight: "600" }}>{m.map}</td>
                        <td style={{ color: "#f0a500" }}>{m.team1_score} – {m.team2_score}</td>
                        <td>
                          <span style={{ color: "#e5e7eb" }}>{m.p1_kills}/{m.p1_deaths}</span>
                          <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}> ({m.p1_rating})</span>
                        </td>
                        <td>
                          <span style={{ color: "#e5e7eb" }}>{m.p2_kills}/{m.p2_deaths}</span>
                          <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}> ({m.p2_rating})</span>
                        </td>
                        <td style={{ color: "#6b7280", fontSize: "0.8rem" }}>
                          {m.finished_at ? new Date(m.finished_at).toLocaleDateString() : "—"}
                        </td>
                        <td><Link to={`/matches/${m.id}`} style={{ color: "#5865f2", fontSize: "0.8rem" }}>View →</Link></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Recent Form Tab */}
          {tab === "form" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
              {[data.player1, data.player2].map((pd, i) => (
                <div key={i} className="card">
                  <h2 style={{ marginBottom: "1rem" }}>{pd.player.name} — Last {pd.recent.length} Matches</h2>
                  {pd.recent.length === 0 ? (
                    <p style={{ color: "#9ca3af" }}>No recent matches.</p>
                  ) : (
                    <table className="table">
                      <thead>
                        <tr><th>Map</th><th>K/D</th><th>ADR</th><th>Rating</th></tr>
                      </thead>
                      <tbody>
                        {pd.recent.map((m, j) => (
                          <tr key={j}>
                            <td style={{ fontSize: "0.85rem" }}>{m.map}</td>
                            <td>{m.kills}/{m.deaths}</td>
                            <td>{m.adr}</td>
                            <td style={{
                              color: m.hltv_rating >= 1.1 ? "#4ade80" : m.hltv_rating >= 0.9 ? "#f0a500" : "#ef4444",
                              fontWeight: "600",
                            }}>{m.hltv_rating}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
